// The Project Files tree, built from the exporter's index instead of
// from the records.
//
// `<Document>_TreeIndex.json.gz` carries the same hierarchy as the
// record file - SubProjects (Category) > Groups (Name) > Components -
// with the components aggregated by name and counted. SpaceX BA03's
// host document is 306 MB of records and 1.5 KB of index for the same
// 220,625 elements.
//
// So the tree is drawn from the index and every row that stands for
// more than one element is left closed, holding only a placeholder
// child. Opening it asks the worker for that row's records
// (helpers/recordWorker.js). Everything below matches the shape
// generateRawTree produces, because TreeViewerItem, the eye toggle,
// findRecursiveInTree and the selection code all read that shape.
import { recordKey, SECTION_TAGS } from './recordWorker';
import { nodeIdOr, scopeTreeIds, scopedNodeId } from "./stableNodeId";

const PREFIX = { S: '', E: ' (Existing) ', D: ' (Demolition) ', X: ' (Disabled) ' };

const SECTIONS = [
  ['S', 'SubProjects'],
  ['E', 'ExistingSubProjects'],
  ['D', 'DemolitionSubprojects'],
  ['X', 'DisabledSubprojects'],
];

export function placeholderId(nodeId) {
  return `${nodeId}::pending`;
}

// A row that stands for `count` elements. It is a section, not a leaf:
// react-simple-tree-menu decides whether to draw an expander from the
// children it can see, so a lazy row carries one placeholder child until
// its records arrive.
function aggregateNode(group, groupId, sub, comp, tag, docId) {
  // THE GROUP'S RESOLVED ID, not its raw `$id` (2026-10-26). This read
  // `${group.$id}::${comp.Name}`, so on an index with no $id - the shape the
  // 2026-10-20 note is about - every group's "3/4 inch" was the same row,
  // "undefined::3/4 inch": one identity shared by every component of that
  // name in the whole file. nodeIdOr was given to the category and group rows
  // then; this row and the file root below were left reading $id raw.
  const id = `${groupId}::${comp.Name}`;
  const count = comp.count || comp.quantity || 0;
  const label = `${PREFIX[tag]}${comp.Name}${count > 1 ? ` (${count})` : ''}`;
  return {
    id,
    parentId: group.$id,
    key: id,
    treeVisible: true,
    label,
    nodes: [
      { id: placeholderId(id), parentId: id, key: placeholderId(id), treeVisible: true, label: 'Loading…', placeholder: true, nodes: [] },
    ],
    lazy: {
      doc: docId,
      key: recordKey(tag, sub.Category, group.Name, comp.Name),
      count,
      prefix: PREFIX[tag],
      loaded: false,
    },
  };
}

// Turn a component record into the leaf the rest of the viewer expects:
// `meshInfo` is the record itself, which is what selection, the quantity
// panel and Component Details read.
// `scope` is the file's docId: a leaf's $id restarts at "1" in every file,
// so it is scoped like every other row (helpers/stableNodeId scopeTreeIds).
export function leafFromRecord(component, parentId, prefix, scope) {
  const id = scopedNodeId(scope, nodeIdOr(component.$id || component.UniqueId, 'comp', parentId, component.Name));
  return {
    id,
    parentId,
    key: id,
    treeVisible: true,
    label: `${prefix || ''}${component.Name}`,
    meshInfo: component,
    nodes: [],
  };
}

// index -> [rootNode], the same single-root array generateRawTree
// returns. `docId` identifies the document to the record worker.
export function generateIndexTree(index, docId) {
  if (!index) return null;
  const nodes = [];
  for (const [tag, field] of SECTIONS) {
    const subprojects = index[field] || [];
    for (const sub of subprojects) {
      const groups = sub.Groups || [];
      // ONLY A COMPONENT HAS AN ID OF ITS OWN (owner, 2026-10-20). A
      // SubProject and a Group exist only as structure, and the `$id` used
      // here is a JSON.NET reference number the serializer assigns - an
      // export written by another path, or a trimmed index, arrives without
      // it, and then every category and group is built with id: undefined.
      // They all collide, the open-node keys become "undefined/undefined"
      // and nothing expands. nodeIdOr keeps the export's id when there is
      // one and otherwise derives a DETERMINISTIC one from what the node is,
      // so rebuilding the tree does not change identity - which a uuid would.
      const subId = nodeIdOr(sub.$id, 'sub', docId, tag, sub.Category);
      nodes.push({
        id: subId,
        parentId: index.$id,
        key: subId,
        treeVisible: true,
        label: `${PREFIX[tag]}${sub.Category}`,
        nodes: groups.map((group) => {
          // Worked out ONCE and passed down: the group row and its component
          // rows have to agree on it, and it was being derived twice.
          const groupId = nodeIdOr(group.$id, 'group', docId, tag, sub.Category, group.Name);
          return {
            id: groupId,
            parentId: subId,
            key: groupId,
            treeVisible: true,
            label: `${PREFIX[tag]}${group.Name}`,
            nodes: (group.Components || []).map((comp) => aggregateNode(group, groupId, sub, comp, tag, docId)),
          };
        }),
      });
    }
  }
  // Scoped by docId: every file's $id restarts at "1" (see scopeTreeIds).
  // THE FILE ROOT NEEDS AN ID AS MUCH AS ANY OTHER ROW (owner, 2026-10-26:
  // "the project files are not replacing the + with the - when a parent is
  // expanded and the first tree is not showing its children"). This was
  // `index.$id` raw - the one node in this builder still reading it - so a
  // trimmed index gave the root id and key `undefined`, and then:
  //   * scopedNodeId passes undefined through, so the root is not scoped to
  //     its file and EVERY file's root carries the same non-identity;
  //   * react-simple-tree-menu composes a row's key from its ancestors'
  //     keys and drops the empty ones, so every file root's key is "" -
  //     one key for all of them, and opening one file opens every file;
  //   * ModelViewer's publishTree force-opens the roots through
  //     `.filter(Boolean)`, which drops them, so the first tree never
  //     opened by itself;
  //   * TreeViewerItem renders id={id}, so the row has no DOM id at all and
  //     the scroll-to-row after a 3D click (getElementById) cannot find it.
  const rootId = nodeIdOr(index.$id, 'doc', docId, index.Title || index.DocumentName || 'Model');
  return scopeTreeIds([
    {
      key: rootId,
      label: index.Title || index.DocumentName || 'Model',
      id: rootId,
      parentId: rootId,
      treeVisible: true,
      // The file itself: its eye hides the whole file by the UniqueId
      // prefixes the page learned for docId (ViewerStateStore.hideDocument).
      documentRoot: true,
      docId,
      nodes,
    },
  ], docId);
}

// Replace a lazy row's placeholder with the records the worker returned.
// Mutates the node in place: the tree is held in React state as an array
// whose identity the caller refreshes, and every other consumer
// (findRecursiveInTree, extractLeafUids) reads `nodes` directly.
export function fillLazyNode(node, result) {
  if (!node || !node.lazy) return node;
  const prefix = node.lazy.prefix;
  const rows = (result && result.components) || [];
  // Which document a record came from, kept on the record itself: the
  // details panel reads its parameters from that document's slice, and
  // a clicked leaf is all the panel is given.
  rows.forEach((component) => { component.__docId = node.lazy.doc; });
  node.nodes = rows.map((component) => leafFromRecord(component, node.id, prefix, node.lazy.doc));
  if (result && result.truncated) {
    const restId = `${node.id}::rest`;
    node.nodes.push({
      id: restId,
      parentId: node.id,
      key: restId,
      treeVisible: true,
      label: `… ${(result.total - rows.length).toLocaleString()} more, pick them in the model`,
      placeholder: true,
      nodes: [],
    });
  }
  // Hiding or selecting the row has to act on every element under it,
  // not only the ones drawn, so the full uid list rides on the node.
  // The full uid list makes hide/select on the row act on every element it
  // stands for. A chunked document may answer with a PAGE of a scattered
  // category and say so; then the row must not claim to act on all of
  // them - acting on the visible leaves only is the honest fallback.
  node.allUids = result && result.uidsComplete === false ? undefined : ((result && result.uids) || []);
  node.lazy = { ...node.lazy, loaded: true };
  return node;
}

// THE CLICKED RECORD MUST BE IN THE ROW IT WAS FOUND UNDER.
//
// A lazy row lists at most MAX_ROWS (2,000) of its elements and ends with
// "... N more, pick them in the model". Picking one in the model is exactly
// what then failed: the worker found the record and the row it sits under,
// but the record was past the first 2,000, so the tree walk returned an
// empty path - "path from pick needed mesh []" - and the page stopped there
// with the tree closed and the details empty (Tom, SpaceX BA03 hanger rods
// and struts, 2026-10-15; a hanger type there runs to thousands).
//
// So the clicked record is put into the row, ahead of the "more" note, when
// it is not already listed. Returns the leaf, or null when the row is not a
// loaded lazy row (a failed or unloaded row is left exactly as it is).
export function ensureLeafForRecord(node, component, opts) {
  // DO NOT EXPAND TWO THOUSAND RECORDS TO SHOW ONE.
  //
  // The caller used to `await loadLazyNode(node)` first, purely to satisfy the
  // `loaded` guard below, and that fetch is the whole remaining delay in a 3D
  // click: the owner's traces of 2026-10-21 put 4,683ms, 4,991ms and 6,309ms
  // between finding the record and picking it, while finding the record itself
  // took 37-64ms. With `allowUnloaded` the clicked row is inserted ahead of
  // the "more" placeholder on a row that has NOT been expanded, which is all
  // the click needs; the rest of the group still loads when the person opens
  // it, and fillLazyNode replaces node.nodes wholesale then, so this row is
  // not duplicated - it comes back as part of the full list.
  const allowUnloaded = !!(opts && opts.allowUnloaded);
  if (!node || !node.lazy || !component) return null;
  if (!node.lazy.loaded && !(allowUnloaded && !node.lazy.failed)) return null;
  const own = component.$id || component.UniqueId;
  if (!own) return null;
  if (!Array.isArray(node.nodes)) node.nodes = [];
  // The same element, already listed: by UniqueId, else by its scoped id.
  const id = scopedNodeId(node.lazy.doc, own);
  const have = node.nodes.find((n) => n && ((component.UniqueId && n.meshInfo && n.meshInfo.UniqueId === component.UniqueId) || n.id === id));
  if (have) return have;
  if (!component.__docId) component.__docId = node.lazy.doc;
  const leaf = leafFromRecord(component, node.id, node.lazy.prefix, node.lazy.doc);
  const firstPlaceholder = node.nodes.findIndex((n) => n && n.placeholder);
  if (firstPlaceholder === -1) node.nodes.push(leaf);
  else node.nodes.splice(firstPlaceholder, 0, leaf);
  return leaf;
}

// Mark a lazy row as failed so the placeholder stops claiming to load.
export function failLazyNode(node, message) {
  if (!node || !node.lazy) return node;
  const failId = `${node.id}::failed`;
  node.nodes = [
    { id: failId, parentId: node.id, key: failId, treeVisible: true, label: message || 'Could not load these elements', placeholder: true, nodes: [] },
  ];
  node.lazy = { ...node.lazy, loaded: false, failed: true };
  return node;
}

export { SECTION_TAGS };

// The lazy row a record belongs to, by the composite key the worker
// hands back with it.
export function findLazyNodeByKey(tree, key) {
  const stack = Array.isArray(tree) ? [...tree] : [tree];
  while (stack.length) {
    const node = stack.pop();
    if (!node) continue;
    if (node.lazy && node.lazy.key === key) return node;
    if (Array.isArray(node.nodes)) for (const child of node.nodes) stack.push(child);
  }
  return null;
}
